import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import Spinner from '@/components/ui/Spinner';
import { db } from '@/lib/firebase';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import ActivityHubComposer from './ActivityHubComposer';

// Standard dictionary of icons per attribute data-type
export const ATTRIBUTE_ICONS = {
    text: 'short_text',
    number: 'tag',
    checkbox: 'check_box',
    date: 'calendar_today',
    rating: 'star',
    select: 'list',
    'multi-select': 'checklist',
    currency: 'attach_money',
    relationship: 'link',
    location: 'place',
    phone: 'phone',
    domain: 'language',
    connection: 'hub'
};

// Standard attributes set per record type
const DEFAULT_ATTRIBUTES = {
    company: [
        { id: 'name', label: 'Name', type: 'text', category: 'General' },
        { id: 'website', label: 'Domain', type: 'domain', category: 'General' },
        { id: 'industry', label: 'Industry', type: 'select', options: ['Technology', 'Healthcare', 'Finance', 'Education', 'Marketing', 'Real Estate'], category: 'General' },
        { id: 'category', label: 'Category', type: 'select', options: ['SaaS', 'B2B', 'B2C', 'Agency', 'Enterprise', 'Hardware'], category: 'General' },
        { id: 'country', label: 'Country', type: 'location', category: 'Location' },
        { id: 'twitterFollowers', label: 'Twitter Followers', type: 'number', category: 'Social' },
        { id: 'arr', label: 'ARR', type: 'currency', category: 'Financial' },
        { id: 'foundedDate', label: 'Founded Date', type: 'date', category: 'General' },
        { id: 'employees', label: 'Employees', type: 'number', category: 'General' },
        { id: 'rating', label: 'Rating', type: 'rating', category: 'General' },
        { id: 'connectionStrength', label: 'Connection Strength', type: 'rating', category: 'General' },
        { id: 'status', label: 'Status', type: 'select', options: ['Active', 'Lead', 'Customer', 'Churned'], category: 'General' }
    ],
    lead: [
        { id: 'name', label: 'Name', type: 'text', category: 'General' },
        { id: 'companyName', label: 'Company', type: 'text', category: 'General' },
        { id: 'email', label: 'Email', type: 'text', category: 'General' },
        { id: 'phone', label: 'Phone', type: 'phone', category: 'General' },
        { id: 'status', label: 'Status', type: 'select', options: ['New', 'Contacted', 'Qualified', 'Lost'], category: 'General' },
        { id: 'industry', label: 'Industry', type: 'select', options: ['Technology', 'Healthcare', 'Finance', 'Education', 'Marketing'], category: 'General' },
        { id: 'source', label: 'Source', type: 'select', options: ['LinkedIn', 'Referral', 'Website', 'Cold Outreach', 'Hubspot'], category: 'General' },
        { id: 'country', label: 'Country', type: 'location', category: 'Location' },
        { id: 'rating', label: 'Rating', type: 'rating', category: 'General' },
        { id: 'connectionStrength', label: 'Connection Strength', type: 'rating', category: 'General' }
    ],
    deal: [
        { id: 'name', label: 'Deal Name', type: 'text', category: 'General' },
        { id: 'company', label: 'Company', type: 'text', category: 'General' },
        { id: 'value', label: 'ARR / Value', type: 'currency', category: 'Financial' },
        { id: 'stage', label: 'Stage', type: 'select', options: ['qualified', 'proposal', 'negotiation', 'closed'], category: 'General' },
        { id: 'owner', label: 'Owner', type: 'text', category: 'General' },
        { id: 'expectedCloseDate', label: 'Expected Close', type: 'date', category: 'General' },
        { id: 'phone', label: 'Phone', type: 'phone', category: 'General' },
        { id: 'email', label: 'Email', type: 'text', category: 'General' },
        { id: 'rating', label: 'Priority / Rating', type: 'rating', category: 'General' },
        { id: 'connectionStrength', label: 'Connection Strength', type: 'rating', category: 'General' }
    ]
};

export default function CRMRecordSidebar({ record, recordType, isOpen, onClose, onUpdate, companyId }) {
    const { user } = useAuth();
    const { getPropertyPermission } = usePermissions();
    const [attributeSearch, setAttributeSearch] = useState('');
    const [activeTab, setActiveTab] = useState('overview');

    const objectKey = recordType === 'deal' ? 'deals' : recordType === 'lead' ? 'leads' : recordType === 'company' ? 'companies' : 'contacts';
    
    // Sub-collections data
    const [activities, setActivities] = useState([]);
    const [emails, setEmails] = useState([]);
    const [calls, setCalls] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [notes, setNotes] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(true);

    // Editing State for left panel key-values
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState('');
    
    // New interaction inputs
    const [noteInput, setNoteInput] = useState('');
    const [emailInput, setEmailInput] = useState({ to: '', subject: '', body: '' });
    const [callInput, setCallInput] = useState({ duration: '', notes: '' });
    const [taskInput, setTaskInput] = useState({ title: '', dueDate: '' });

    const effectiveCompanyId = companyId || record?.companyId || 'default_tenant';

    // Seed dynamic attributes registry from LocalStorage if user created custom attributes
    const [customAttributes, setCustomAttributes] = useState(() => {
        const saved = localStorage.getItem(`crm_custom_attrs_${recordType}`);
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => {
        const handleStorageChange = () => {
            const saved = localStorage.getItem(`crm_custom_attrs_${recordType}`);
            if (saved) setCustomAttributes(JSON.parse(saved));
        };
        window.addEventListener('crm_custom_attributes_changed', handleStorageChange);
        return () => window.removeEventListener('crm_custom_attributes_changed', handleStorageChange);
    }, [recordType]);

    const attributes = useMemo(() => {
        return [...(DEFAULT_ATTRIBUTES[recordType] || []), ...customAttributes];
    }, [recordType, customAttributes]);

    // Fetch activities, emails, calls, notes, tasks on record change
    useEffect(() => {
        if (!isOpen || !record?.id || !effectiveCompanyId) return;

        setLoadingActivities(true);
        const basePath = `companies/${effectiveCompanyId}/${recordType}s/${record.id}`;
        
        // 1. Activities snapshot
        const qAct = query(collection(db, `${basePath}/activities`), orderBy('timestamp', 'desc'));
        const unsubAct = onSnapshot(qAct, (snap) => {
            setActivities(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoadingActivities(false);
        }, () => setLoadingActivities(false));

        // 2. Emails snapshot
        const qEmail = query(collection(db, `${basePath}/emails`), orderBy('timestamp', 'desc'));
        const unsubEmail = onSnapshot(qEmail, (snap) => {
            setEmails(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // 3. Calls snapshot
        const qCall = query(collection(db, `${basePath}/calls`), orderBy('timestamp', 'desc'));
        const unsubCall = onSnapshot(qCall, (snap) => {
            setCalls(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // 4. Meetings snapshot
        const qMeeting = query(collection(db, `${basePath}/meetings`), orderBy('timestamp', 'desc'));
        const unsubMeeting = onSnapshot(qMeeting, (snap) => {
            setMeetings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // 5. Notes snapshot
        const qNote = query(collection(db, `${basePath}/notes`), orderBy('timestamp', 'desc'));
        const unsubNote = onSnapshot(qNote, (snap) => {
            setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // 6. Tasks snapshot
        const qTask = query(collection(db, `${basePath}/tasks`), orderBy('timestamp', 'desc'));
        const unsubTask = onSnapshot(qTask, (snap) => {
            setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => {
            unsubAct();
            unsubEmail();
            unsubCall();
            unsubMeeting();
            unsubNote();
            unsubTask();
        };
    }, [isOpen, record?.id, recordType, effectiveCompanyId]);

    if (!isOpen || !record) return null;

    // Filter attributes based on left-panel search
    const filteredAttributes = attributes.filter(attr => {
        if (!attributeSearch) return true;
        return attr.label.toLowerCase().includes(attributeSearch.toLowerCase());
    });

    const handleFieldSave = async (fieldId, value) => {
        let typedVal = value;
        // Parse numbers & currency
        const attr = attributes.find(a => a.id === fieldId);
        if (attr) {
            if (attr.type === 'number') typedVal = parseFloat(value) || 0;
            if (attr.type === 'currency') typedVal = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
        }

        try {
            // Update Firestore
            const recordRef = doc(db, `companies/${effectiveCompanyId}/${recordType}s`, record.id);
            const updatePayload = { [fieldId]: typedVal };
            
            // Mirror logic for stats fields
            if (fieldId === 'arr') {
                updatePayload['stats'] = { ...record.stats, totalValue: typedVal };
            }
            if (fieldId === 'status') {
                updatePayload['status'] = typedVal;
            }

            await updateDoc(recordRef, updatePayload);

            // Log activity
            await addDoc(collection(db, `companies/${effectiveCompanyId}/${recordType}s/${record.id}/activities`), {
                type: 'System',
                text: `Changed ${attr?.label || fieldId} to "${typedVal}"`,
                timestamp: serverTimestamp(),
                user: user?.email || 'Arunima'
            });

            if (onUpdate) onUpdate({ ...record, ...updatePayload });
        } catch (e) {
            console.error("Failed to update attribute:", e);
        }
        setEditingField(null);
    };

    const handleLogNote = async (e) => {
        e.preventDefault();
        if (!noteInput.trim()) return;

        try {
            const basePath = `companies/${effectiveCompanyId}/${recordType}s/${record.id}`;
            await addDoc(collection(db, `${basePath}/notes`), {
                text: noteInput,
                timestamp: serverTimestamp(),
                user: user?.email || 'Arunima'
            });
            await addDoc(collection(db, `${basePath}/activities`), {
                type: 'Note',
                text: `Added note: "${noteInput.substring(0, 60)}${noteInput.length > 60 ? '...' : ''}"`,
                timestamp: serverTimestamp(),
                user: user?.email || 'Arunima'
            });
            setNoteInput('');
            setActiveTab('overview');
        } catch (err) {
            console.error("Failed to save note:", err);
        }
    };

    const handleSaveUniversalActivity = async (payload) => {
        try {
            const basePath = `companies/${effectiveCompanyId}/${recordType}s/${record.id}`;
            const actType = payload.type || 'Activity';

            if (actType === 'Email') {
                await addDoc(collection(db, `${basePath}/emails`), {
                    to: payload.to || '',
                    subject: payload.subject || '',
                    body: payload.body || '',
                    tracked: payload.tracked ?? true,
                    status: payload.status || 'sent',
                    timestamp: serverTimestamp(),
                    user: payload.user || user?.email || 'Arunima'
                });
            } else if (actType === 'Call') {
                await addDoc(collection(db, `${basePath}/calls`), {
                    phone: payload.phone || '',
                    duration: payload.duration || '5:00',
                    disposition: payload.disposition || 'Connected',
                    notes: payload.notes || '',
                    timestamp: serverTimestamp(),
                    user: payload.user || user?.email || 'Arunima'
                });
            } else if (actType === 'Meeting') {
                await addDoc(collection(db, `${basePath}/meetings`), {
                    subject: payload.subject || '',
                    date: payload.date || '',
                    time: payload.time || '',
                    duration: payload.duration || '30 mins',
                    meetUrl: payload.meetUrl || '',
                    agenda: payload.agenda || '',
                    timestamp: serverTimestamp(),
                    user: payload.user || user?.email || 'Arunima'
                });
            }

            // Main Record Activities Timeline
            await addDoc(collection(db, `${basePath}/activities`), {
                type: actType,
                text: payload.text || `${actType} logged`,
                timestamp: serverTimestamp(),
                user: payload.user || user?.email || 'Arunima'
            });

            // Mirror to Company level activities feed for CRM Communication Page
            await addDoc(collection(db, `companies/${effectiveCompanyId}/activities`), {
                type: actType.toLowerCase(),
                recordId: record.id,
                recordType: recordType,
                subject: payload.subject || payload.text || `${actType} logged`,
                contact: payload.to || payload.phone || record?.contact || record?.name || 'Contact',
                company: record?.company || record?.name || '',
                duration: payload.duration || '',
                notes: payload.notes || payload.body || payload.agenda || '',
                status: 'completed',
                createdAt: serverTimestamp(),
                user: payload.user || user?.email || 'Arunima'
            });

            setActiveTab('overview');
        } catch (err) {
            console.error("Failed to save universal activity:", err);
        }
    };

    const handleSendEmail = async (e) => {
        e.preventDefault();
        if (!emailInput.to.trim()) return;

        try {
            const basePath = `companies/${effectiveCompanyId}/${recordType}s/${record.id}`;
            await addDoc(collection(db, `${basePath}/emails`), {
                ...emailInput,
                timestamp: serverTimestamp(),
                user: user?.email || 'Arunima'
            });
            await addDoc(collection(db, `${basePath}/activities`), {
                type: 'Email',
                text: `Sent email to ${emailInput.to}: "${emailInput.subject}"`,
                timestamp: serverTimestamp(),
                user: user?.email || 'Arunima'
            });
            setEmailInput({ to: '', subject: '', body: '' });
            setActiveTab('overview');
        } catch (err) {
            console.error("Failed to log email:", err);
        }
    };

    const handleLogCall = async (e) => {
        e.preventDefault();
        try {
            const basePath = `companies/${effectiveCompanyId}/${recordType}s/${record.id}`;
            await addDoc(collection(db, `${basePath}/calls`), {
                duration: callInput.duration || '5 mins',
                notes: callInput.notes,
                timestamp: serverTimestamp(),
                user: user?.email || 'Arunima'
            });
            await addDoc(collection(db, `${basePath}/activities`), {
                type: 'Call',
                text: `Logged call (${callInput.duration || '5 mins'}): "${callInput.notes}"`,
                timestamp: serverTimestamp(),
                user: user?.email || 'Arunima'
            });
            setCallInput({ duration: '', notes: '' });
            setActiveTab('overview');
        } catch (err) {
            console.error("Failed to log call:", err);
        }
    };

    const handleCreateTask = async (e) => {
        e.preventDefault();
        if (!taskInput.title.trim()) return;

        try {
            const basePath = `companies/${effectiveCompanyId}/${recordType}s/${record.id}`;
            await addDoc(collection(db, `${basePath}/tasks`), {
                title: taskInput.title,
                dueDate: taskInput.dueDate || '',
                completed: false,
                timestamp: serverTimestamp(),
                user: user?.email || 'Arunima'
            });
            await addDoc(collection(db, `${basePath}/activities`), {
                type: 'Task',
                text: `Created task: "${taskInput.title}"`,
                timestamp: serverTimestamp(),
                user: user?.email || 'Arunima'
            });
            setTaskInput({ title: '', dueDate: '' });
            setActiveTab('overview');
        } catch (err) {
            console.error("Failed to create task:", err);
        }
    };

    const renderBentoHighlights = () => {
        if (recordType === 'company') {
            const arr = record.arr || record.stats?.totalValue || 0;
            const employees = record.employees || 0;
            const stage = record.status || 'Lead';
            
            return (
                <div style={bentoGridStyle}>
                    <div style={bentoCardStyle('#eff6ff', '#2563eb')}>
                        <span style={bentoLabelStyle}>ARR / Valuation</span>
                        <span style={bentoValueStyle}>${arr.toLocaleString()}</span>
                    </div>
                    <div style={bentoCardStyle('#f0fdf4', '#16a34a')}>
                        <span style={bentoLabelStyle}>Connection Strength</span>
                        <div style={{ display: 'flex', gap: '2px', marginTop: '6px' }}>
                            {[1, 2, 3, 4, 5].map(star => (
                                <ObliqueIcon
                                    key={star}
                                    name="star"
                                    size={16}
                                    color={star <= (record.connectionStrength || 4) ? '#16a34a' : '#d1d5db'}
                                />
                            ))}
                        </div>
                    </div>
                    <div style={bentoCardStyle('#faf5ff', '#9333ea')}>
                        <span style={bentoLabelStyle}>Employee Count</span>
                        <span style={bentoValueStyle}>{employees ? `${employees} people` : '10 - 50'}</span>
                    </div>
                    <div style={bentoCardStyle('#fffbeb', '#d97706')}>
                        <span style={bentoLabelStyle}>Lifecycle Stage</span>
                        <span style={{ ...bentoValueStyle, fontSize: '15px', textTransform: 'capitalize' }}>{stage}</span>
                    </div>
                </div>
            );
        } else if (recordType === 'lead') {
            return (
                <div style={bentoGridStyle}>
                    <div style={bentoCardStyle('#eff6ff', '#2563eb')}>
                        <span style={bentoLabelStyle}>Status</span>
                        <span style={bentoValueStyle}>{record.status || 'New'}</span>
                    </div>
                    <div style={bentoCardStyle('#f0fdf4', '#16a34a')}>
                        <span style={bentoLabelStyle}>Rating</span>
                        <div style={{ display: 'flex', gap: '2px', marginTop: '6px' }}>
                            {[1, 2, 3, 4, 5].map(star => (
                                <ObliqueIcon
                                    key={star}
                                    name="star"
                                    size={16}
                                    color={star <= (record.rating || 3) ? '#16a34a' : '#d1d5db'}
                                />
                            ))}
                        </div>
                    </div>
                    <div style={bentoCardStyle('#faf5ff', '#9333ea')}>
                        <span style={bentoLabelStyle}>Source</span>
                        <span style={{ ...bentoValueStyle, fontSize: '15px' }}>{record.source || 'Direct Outreach'}</span>
                    </div>
                    <div style={bentoCardStyle('#fffbeb', '#d97706')}>
                        <span style={bentoLabelStyle}>Country</span>
                        <span style={{ ...bentoValueStyle, fontSize: '15px' }}>{record.country || 'USA'}</span>
                    </div>
                </div>
            );
        } else {
            // Deal
            return (
                <div style={bentoGridStyle}>
                    <div style={bentoCardStyle('#f0fdf4', '#16a34a')}>
                        <span style={bentoLabelStyle}>Deal Value</span>
                        <span style={bentoValueStyle}>${(record.value || 0).toLocaleString()}</span>
                    </div>
                    <div style={bentoCardStyle('#eff6ff', '#2563eb')}>
                        <span style={bentoLabelStyle}>Pipeline Stage</span>
                        <span style={{ ...bentoValueStyle, fontSize: '15px', textTransform: 'capitalize' }}>{record.stage || 'Qualified'}</span>
                    </div>
                    <div style={bentoCardStyle('#faf5ff', '#9333ea')}>
                        <span style={bentoLabelStyle}>Deal Owner</span>
                        <span style={{ ...bentoValueStyle, fontSize: '15px' }}>{record.owner || 'Unassigned'}</span>
                    </div>
                    <div style={bentoCardStyle('#fffbeb', '#d97706')}>
                        <span style={bentoLabelStyle}>Expected Close</span>
                        <span style={{ ...bentoValueStyle, fontSize: '14px' }}>{record.expectedCloseDate || 'Not set'}</span>
                    </div>
                </div>
            );
        }
    };

    return (
        <div style={backdropStyle} onClick={onClose}>
            <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                style={drawerStyle}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={headerStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={avatarStyle}>
                            <ObliqueIcon 
                                name={recordType === 'company' ? 'business' : recordType === 'lead' ? 'person' : 'payments'} 
                                size={18} 
                                color="var(--accent)"
                            />
                        </div>
                        <div>
                            <h2 style={titleStyle}>{record.name || record.companyName || 'Untitled'}</h2>
                            <span style={subtitleStyle}>{recordType.toUpperCase()} • {record.industry || record.stage || 'General'}</span>
                        </div>
                    </div>
                    
                    <button style={closeBtnStyle} onClick={onClose}>
                        <ObliqueIcon name="close" size={20} color="var(--text-secondary)" />
                    </button>
                </div>

                {/* Double Panel Workspace */}
                <div style={workspaceStyle}>
                    {/* LEFT PANEL: Dense Attributes key-value configuration */}
                    <div style={leftPanelStyle}>
                        <div style={searchContainerStyle}>
                            <ObliqueIcon name="search" size={14} color="var(--text-tertiary)" />
                            <input
                                type="text"
                                placeholder="Search attributes..."
                                value={attributeSearch}
                                onChange={e => setAttributeSearch(e.target.value)}
                                style={searchInputStyle}
                            />
                        </div>

                        <div style={attributesListStyle} className="custom-scrollbar">
                            {filteredAttributes.map(attr => {
                                const val = record[attr.id];
                                const isEditing = editingField === attr.id;
                                const isEmpty = val === undefined || val === null || val === '';
                                const propPerm = getPropertyPermission(objectKey, attr.id);
                                const isRestricted = propPerm === 'restricted';
                                const canEdit = propPerm === 'edit';

                                return (
                                    <div key={attr.id} style={attrRowStyle}>
                                        <div style={attrLabelStyle}>
                                            <ObliqueIcon name={ATTRIBUTE_ICONS[attr.type] || 'description'} size={13} color="var(--text-tertiary)" />
                                            <span>{attr.label}</span>
                                        </div>

                                        <div style={attrValueContainerStyle}>
                                            {isRestricted ? (
                                                <span style={{ ...attrValueStyle, color: 'var(--text-tertiary)', cursor: 'not-allowed', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    •••••••• 
                                                    <span style={{ fontSize: '10px', background: 'var(--bg-muted)', color: 'var(--text-tertiary)', padding: '1px 5px', borderRadius: '4px' }}>Restricted</span>
                                                </span>
                                            ) : isEditing ? (
                                                attr.type === 'select' ? (
                                                    <select
                                                        autoFocus
                                                        value={editValue}
                                                        onChange={e => handleFieldSave(attr.id, e.target.value)}
                                                        onBlur={e => handleFieldSave(attr.id, e.target.value)}
                                                        style={inlineInputStyle}
                                                    >
                                                        <option value="">Select option...</option>
                                                        {attr.options?.map(opt => (
                                                            <option key={opt} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={editValue}
                                                        onChange={e => setEditValue(e.target.value)}
                                                        onBlur={e => handleFieldSave(attr.id, e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') handleFieldSave(attr.id, editValue);
                                                            if (e.key === 'Escape') setEditingField(null);
                                                        }}
                                                        style={inlineInputStyle}
                                                    />
                                                )
                                            ) : isEmpty ? (
                                                canEdit ? (
                                                    <button
                                                        onClick={() => {
                                                            setEditingField(attr.id);
                                                            setEditValue(val || '');
                                                        }}
                                                        style={setValueStyle}
                                                    >
                                                        Set a value...
                                                    </button>
                                                ) : (
                                                    <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>-</span>
                                                )
                                            ) : (
                                                <span
                                                    onClick={() => {
                                                        if (!canEdit) return;
                                                        setEditingField(attr.id);
                                                        setEditValue(String(val));
                                                    }}
                                                    style={{
                                                        ...attrValueStyle,
                                                        cursor: canEdit ? 'pointer' : 'default',
                                                        opacity: canEdit ? 1 : 0.85,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                    title={canEdit ? 'Click to edit' : 'Field is read-only for your permission set'}
                                                >
                                                    {attr.type === 'currency' ? `$${parseFloat(val).toLocaleString()}` : String(val)}
                                                    {!canEdit && (
                                                        <ObliqueIcon name="lock" size={12} color="var(--text-tertiary)" />
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {filteredAttributes.length === 0 && (
                                <div style={{ padding: '24px 8px', fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
                                    No matching attributes found.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT PANEL: Tabbed metrics overview & history logs */}
                    <div style={rightPanelStyle}>
                        {/* Tab Bar */}
                        <div style={tabBarStyle}>
                            {[
                                { id: 'overview', label: 'Overview', icon: 'visibility', count: null },
                                { id: 'emails', label: 'Emails', icon: 'mail', count: emails.length },
                                { id: 'calls', label: 'Calls', icon: 'phone', count: calls.length },
                                { id: 'meetings', label: 'Meetings', icon: 'videocam', count: meetings.length },
                                { id: 'notes', label: 'Notes', icon: 'note', count: notes.length },
                                { id: 'tasks', label: 'Tasks', icon: 'check_box', count: tasks.length }
                            ].map(tab => {
                                const isActive = activeTab === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        style={tabBtnStyle(isActive)}
                                    >
                                        <ObliqueIcon name={tab.icon} size={15} color={isActive ? 'var(--accent)' : 'var(--text-secondary)'} />
                                        <span>{tab.label}</span>
                                        {tab.count !== null && tab.count > 0 && (
                                            <span style={tabBadgeStyle(isActive)}>{tab.count}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Content Area */}
                        <div style={tabContentStyle} className="custom-scrollbar">
                            {activeTab === 'overview' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {/* Bento Grid */}
                                    {renderBentoHighlights()}

                                    {/* Activity Feed Title */}
                                    <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '20px', marginTop: '8px' }}>
                                        <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <ObliqueIcon name="history" size={16} color="var(--accent)" />
                                            <span>Activities Feed</span>
                                        </h3>

                                        {loadingActivities ? (
                                            /* Beautiful quiet Timeline Skeletons */
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                {[1, 2, 3].map(i => (
                                                    <div key={i} style={skeletonRowStyle}>
                                                        <div style={skeletonDotStyle} />
                                                        <div style={{ flex: 1 }}>
                                                            <div style={skeletonLineStyle('60%', '12px')} />
                                                            <div style={skeletonLineStyle('40%', '8px', '6px')} />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : activities.length === 0 ? (
                                            <div style={emptyTimelineStyle}>
                                                <ObliqueIcon name="history" size={28} color="var(--text-tertiary)" style={{ opacity: 0.5 }} />
                                                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>No activities logged yet.</span>
                                            </div>
                                        ) : (
                                            <div style={timelineContainerStyle}>
                                                {activities.map((act, index) => (
                                                    <div key={act.id} style={{ display: 'flex', gap: '12px', position: 'relative', paddingBottom: index === activities.length - 1 ? 0 : '16px' }}>
                                                        {index !== activities.length - 1 && <div style={timelineLineStyle} />}
                                                        
                                                        <div style={timelineIconStyle(act.type)}>
                                                            <ObliqueIcon 
                                                                name={act.type === 'Meeting' ? 'videocam' : act.type === 'Note' ? 'note' : act.type === 'Email' ? 'mail' : act.type === 'Call' ? 'phone' : act.type === 'Task' ? 'check_box' : 'flash_on'} 
                                                                size={12} 
                                                                color="#fff" 
                                                            />
                                                        </div>
                                                        <div style={timelineBodyStyle}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                                <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>{act.text}</span>
                                                                <span style={{ fontSize: '10.5px', color: 'var(--text-tertiary)' }}>
                                                                    {act.timestamp?.toDate ? act.timestamp.toDate().toLocaleDateString() : new Date().toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px', display: 'block' }}>
                                                                Done by <strong style={{ color: 'var(--text-secondary)' }}>{act.user || 'Arunima'}</strong>
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'notes' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <form onSubmit={handleLogNote} style={formContainerStyle}>
                                        <textarea
                                            placeholder="Write a private note..."
                                            value={noteInput}
                                            onChange={e => setNoteInput(e.target.value)}
                                            style={textareaStyle}
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                            <button type="submit" className="btn btn-primary" style={{ padding: '6px 12px', height: '30px', fontSize: '12px' }}>
                                                Save Note
                                            </button>
                                        </div>
                                    </form>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {notes.map(n => (
                                            <div key={n.id} style={noteCardStyle}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--accent)' }}>{n.user}</span>
                                                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{n.timestamp?.toDate ? n.timestamp.toDate().toLocaleString() : ''}</span>
                                                </div>
                                                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{n.text}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'emails' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <ActivityHubComposer 
                                        initialTab="email" 
                                        record={record} 
                                        recordType={recordType} 
                                        onSaveActivity={handleSaveUniversalActivity} 
                                    />

                                    {emails.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Email History ({emails.length})
                                            </div>
                                            {emails.map(e => (
                                                <div key={e.id} style={noteCardStyle}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                        <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>To: {e.to}</span>
                                                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{e.timestamp?.toDate ? e.timestamp.toDate().toLocaleString() : ''}</span>
                                                    </div>
                                                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>{e.subject}</div>
                                                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{e.body}</p>
                                                    {e.tracked && (
                                                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', color: '#10b981', fontWeight: '600' }}>
                                                            <ObliqueIcon name="visibility" size={12} /> Live tracking active
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'calls' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <ActivityHubComposer 
                                        initialTab="call" 
                                        record={record} 
                                        recordType={recordType} 
                                        onSaveActivity={handleSaveUniversalActivity} 
                                    />

                                    {calls.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Call History ({calls.length})
                                            </div>
                                            {calls.map(c => (
                                                <div key={c.id} style={noteCardStyle}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                        <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <ObliqueIcon name="phone" size={12} /> {c.duration || '5m'}
                                                            {c.disposition && (
                                                                <span style={{ background: 'var(--accent-subtle)', color: 'var(--accent)', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>
                                                                    {c.disposition}
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{c.timestamp?.toDate ? c.timestamp.toDate().toLocaleString() : ''}</span>
                                                    </div>
                                                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{c.notes}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'meetings' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <ActivityHubComposer 
                                        initialTab="meeting" 
                                        record={record} 
                                        recordType={recordType} 
                                        onSaveActivity={handleSaveUniversalActivity} 
                                    />

                                    {meetings.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Scheduled Meetings ({meetings.length})
                                            </div>
                                            {meetings.map(m => (
                                                <div key={m.id} style={noteCardStyle}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                            <ObliqueIcon name="videocam" size={14} /> {m.subject}
                                                        </span>
                                                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                                                            {m.date} at {m.time} ({m.duration || '30m'})
                                                        </span>
                                                    </div>
                                                    {m.agenda && (
                                                        <p style={{ margin: '0 0 10px 0', fontSize: '12.5px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{m.agenda}</p>
                                                    )}
                                                    {m.meetUrl && (
                                                        <a 
                                                            href={m.meetUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                padding: '6px 12px',
                                                                background: '#8b5cf6',
                                                                color: '#ffffff',
                                                                borderRadius: '8px',
                                                                fontSize: '11.5px',
                                                                fontWeight: '600',
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            <ObliqueIcon name="videocam" size={13} color="#fff" />
                                                            Launch Google Meet
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'tasks' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <form onSubmit={handleCreateTask} style={formContainerStyle}>
                                        <input
                                            type="text"
                                            placeholder="Task title..."
                                            required
                                            value={taskInput.title}
                                            onChange={e => setTaskInput({ ...taskInput, title: e.target.value })}
                                            style={inputStyle}
                                        />
                                        <input
                                            type="date"
                                            value={taskInput.dueDate}
                                            onChange={e => setTaskInput({ ...taskInput, dueDate: e.target.value })}
                                            style={{ ...inputStyle, marginTop: '8px' }}
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                            <button type="submit" className="btn btn-primary" style={{ padding: '6px 12px', height: '30px', fontSize: '12px' }}>
                                                Add Task
                                            </button>
                                        </div>
                                    </form>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {tasks.map(t => (
                                            <div key={t.id} style={{ ...noteCardStyle, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={t.completed}
                                                    onChange={async (e) => {
                                                        const docRef = doc(db, `companies/${effectiveCompanyId}/${recordType}s/${record.id}/tasks`, t.id);
                                                        await updateDoc(docRef, { completed: e.target.checked });
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <span style={{ fontSize: '13px', fontWeight: '500', color: t.completed ? 'var(--text-tertiary)' : 'var(--text-primary)', textDecoration: t.completed ? 'line-through' : 'none' }}>
                                                        {t.title}
                                                    </span>
                                                    {t.dueDate && (
                                                        <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                                            Due by {t.dueDate}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

// ─── STYLES ─────────────────────────────────────────

const backdropStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(4px)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'flex-end'
};

const drawerStyle = {
    width: '900px',
    height: '100%',
    background: 'var(--bg-surface)',
    borderLeft: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.15)'
};

const headerStyle = {
    height: '72px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    flexShrink: 0
};

const avatarStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'var(--accent-subtle)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'inset 0 0 0 1px var(--accent-border)'
};

const titleStyle = {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    lineHeight: '1.2'
};

const subtitleStyle = {
    fontSize: '11px',
    fontWeight: '500',
    color: 'var(--text-tertiary)',
    letterSpacing: '0.03em'
};

const closeBtnStyle = {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s'
};

const workspaceStyle = {
    flex: 1,
    display: 'flex',
    overflow: 'hidden'
};

const leftPanelStyle = {
    width: '320px',
    borderRight: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--bg-subtle)'
};

const searchContainerStyle = {
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'var(--bg-surface)'
};

const searchInputStyle = {
    border: 'none',
    background: 'transparent',
    fontSize: '12px',
    color: 'var(--text-primary)',
    outline: 'none',
    width: '100%',
    boxShadow: 'none',
    padding: 0
};

const attributesListStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 0'
};

const attrRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 16px',
    minHeight: '36px',
    borderBottom: '1px solid var(--border-subtle)'
};

const attrLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    fontWeight: 500,
    width: '130px',
    flexShrink: 0
};

const attrValueContainerStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    overflow: 'hidden'
};

const inlineInputStyle = {
    width: '100%',
    padding: '3px 8px',
    fontSize: '12px',
    border: '1px solid var(--accent)',
    borderRadius: '4px',
    background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    outline: 'none',
    boxShadow: '0 0 0 2px var(--accent-subtle)'
};

const attrValueStyle = {
    fontSize: '12.5px',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '100%',
    fontWeight: 500
};

const setValueStyle = {
    border: 'none',
    background: 'transparent',
    color: 'var(--text-tertiary)',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px',
    fontStyle: 'italic'
};

const rightPanelStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--bg-surface)'
};

const tabBarStyle = {
    height: '42px',
    borderBottom: '1px solid var(--border-subtle)',
    display: 'flex',
    gap: '4px',
    padding: '0 16px',
    alignItems: 'flex-end',
    flexShrink: 0
};

const tabBtnStyle = (active) => ({
    height: '36px',
    border: 'none',
    background: 'transparent',
    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
    fontSize: '12.5px',
    fontWeight: active ? '600' : '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0 12px',
    transition: 'all 0.15s'
});

const tabBadgeStyle = (active) => ({
    fontSize: '10.5px',
    padding: '1px 6px',
    borderRadius: '100px',
    background: active ? 'var(--accent-subtle)' : 'var(--bg-muted)',
    color: active ? 'var(--accent)' : 'var(--text-tertiary)',
    marginLeft: '4px',
    fontWeight: '600'
});

const tabContentStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '24px'
};

const bentoGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px'
};

const bentoCardStyle = (bgColor, color) => ({
    background: bgColor,
    border: `1px solid ${color}15`,
    borderRadius: '10px',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 1px 3px rgba(0,0,0,0.01)',
    transition: 'transform 0.15s ease'
});

const bentoLabelStyle = {
    fontSize: '11px',
    textTransform: 'uppercase',
    fontWeight: '600',
    color: 'var(--text-tertiary)',
    letterSpacing: '0.05em'
};

const bentoValueStyle = {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginTop: '4px'
};

const timelineContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
};

const timelineLineStyle = {
    position: 'absolute',
    left: '11px',
    top: '24px',
    bottom: 0,
    width: '1.5px',
    background: 'var(--border-subtle)'
};

const timelineIconStyle = (type) => {
    let bg = 'var(--accent)';
    if (type === 'Email') bg = '#10b981';
    if (type === 'Call') bg = '#f59e0b';
    if (type === 'Meeting') bg = '#8b5cf6';
    if (type === 'Task') bg = '#3b82f6';
    
    return {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1
    };
};

const timelineBodyStyle = {
    background: 'var(--bg-subtle)',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid var(--border-subtle)',
    flex: 1
};

const emptyTimelineStyle = {
    padding: '32px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px'
};

const skeletonRowStyle = {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    padding: '8px 0'
};

const skeletonDotStyle = {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: 'var(--bg-muted)',
    opacity: 0.7,
    animation: 'pulse 1.5s infinite ease-in-out'
};

const skeletonLineStyle = (width, height, marginTop = '0px') => ({
    width,
    height,
    background: 'var(--bg-muted)',
    borderRadius: '4px',
    marginTop,
    opacity: 0.7,
    animation: 'pulse 1.5s infinite ease-in-out'
});

const formContainerStyle = {
    background: 'var(--bg-subtle)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
};

const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    fontSize: '12.5px',
    color: 'var(--text-primary)',
    outline: 'none',
    boxShadow: 'none'
};

const textareaStyle = {
    width: '100%',
    minHeight: '80px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    fontSize: '12.5px',
    color: 'var(--text-primary)',
    outline: 'none',
    resize: 'none',
    boxShadow: 'none'
};

const noteCardStyle = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '12px 14px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.01)'
};
